import { Component, inject } from '@angular/core';
import { UserService } from '@tytapp/user';
import { PageComponent, Transfer } from '@tytapp/common';
import { ActivatedRoute } from '@angular/router';
import { ApiArticle, ApiLiveSchedule, ApiLiveScheduleList, ApiLiveStream, ApiProduction, ApiUser } from '@tytapp/api';
import { LiveStreamsService, ProductionsService, ArticlesService } from '@tytapp/common-ui';
import { isClientSide, isServerSide } from '@tytapp/environment-utils';

@Component({
    selector: 'tyt-live-schedule-page',
    templateUrl: './live-schedule-page.component.html',
    styleUrls: ['./live-schedule-page.component.scss']
})
export class LiveSchedulePageComponent extends PageComponent {
    private route = inject(ActivatedRoute, { optional: true });
    private userService = inject(UserService);
    private productionsService = inject(ProductionsService);
    private articlesService = inject(ArticlesService);
    private liveStreamsService = inject(LiveStreamsService);

    get pageComponentId() {
        return 'live-page';
    }

    @Transfer()
    allStreams: ApiLiveStream[];

    @Transfer()
    featuredStream: ApiLiveStream;

    @Transfer()
    otherStreams: ApiLiveStream[];

    @Transfer()
    user: ApiUser;

    @Transfer()
    liveSchedules: ApiLiveScheduleList[];

    @Transfer()
    recentEpisodes: ApiProduction[];

    @Transfer()
    recentArticles: ApiArticle[];

    @Transfer()
    ready: boolean = false;

    @Transfer()
    timezoneString = null;

    audienceType: string = 'visitor';

    async getRecentArticles() {
        let recentArticles = null;
        try {
            recentArticles = <ApiArticle[]>await this.articlesService.all();

            return recentArticles;
        } catch (error) {
            if (error.json) {
                error = error.json();
            }

            console.error(`Caught exception while fetching articles:`);
            console.error(error);
            return null;
        }
    }

    getEventStream(event: ApiLiveSchedule): ApiLiveStream {
        if (!this.allStreams)
            return null;

        let streams = this.allStreams.filter(x => x.active_live_event && x.active_live_event.id == event.id);
        let premiumStream = streams.filter(x => x.premium)[0];
        let publicStream = streams.filter(x => !x.premium)[0];

        if (this.user && this.user.entitled)
            return premiumStream || publicStream;

        return publicStream || premiumStream;
    }

    @Transfer()
    timezone: string = null;

    async init() {
        this.pageTitle = 'Live Schedule';
        this.emulateNow = new Date('2019-03-22T03:00:00-0800').getTime();
        this.head.setCanonicalLink(`/live/schedule`);

        this.subscribe(this.userService.userChanged, user => {
            this.user = user;
            this.fetchData();
        });

        if (this.route) this.subscribe(this.route.data, data => {
            this.fetchData();
        });

        this.audienceType = this.userService.audienceType;
        await this.fetchSchedule();
    }

    emulateNow: number;

    isEnded(event: ApiLiveSchedule) {
        if (this.getEventStream(event))
            return false;

        let now = Date.now();

        if (this.emulateNow !== undefined)
            now = this.emulateNow;

        let startedAt = 1000 * parseInt(event.air_time_unix);
        return startedAt + 1000 * 60 * 15 < now;
    }

    getTimezoneOffset(d, tz) {
        var a = d.toLocaleString("ja", { timeZone: tz }).split(/[\/\s:]/);
        a[1]--;
        var t1 = Date.UTC.apply(null, a);
        var t2 = new Date(d).setMilliseconds(0);
        return (t2 - t1) / 60 / 1000 / 60;
    }

    async fetchSchedule() {
        let options: Record<string, string> = { timeZoneName: 'short' };
        if (isServerSide()) {
            options['timeZone'] = 'America/New_York';
        }

        if (this.useTimezone === 'pst') {
            options['timeZone'] = 'America/Los_Angeles';
        }

        if (this.useTimezone === 'est') {
            options['timeZone'] = 'America/New_York';
        }

        let customOffset = undefined;
        if (options['timeZone']) {
            customOffset = this.getTimezoneOffset(new Date(), options['timeZone']);
        }

        let date = new Date();
        this.timezoneString = date.toLocaleTimeString('en-us', options).split(' ')[2];
        if (isServerSide() || this.useTimezone != 'local')
            this.timezone = this.timezoneString;
        else
            this.timezone = null;

        if (!this.hasServerState) {
            await Promise.all([
                this.productionsService.latestVODs(0, 1),
                this.getRecentArticles()
            ]).then(results => {
                this.recentEpisodes = results[0];
                this.recentArticles = results[1];
            });
        }

        let schedules = await this.liveStreamsService.getSchedule(customOffset);
        if (schedules) {
            let todayIndex = new Date().getDay();
            todayIndex = 1;

            let days = [
                'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday',
                'Friday', 'Saturday'
            ];
            let reordered = [];
            for (let day = 0; day < 7; ++day) {
                let dayStr = days[(todayIndex + day) % 7].toLowerCase();
                let scheduleItem = schedules.find(x => x.day.toLowerCase() === dayStr);
                if (scheduleItem)
                    reordered.push(scheduleItem);
            }
            schedules = reordered;

            this.liveSchedules = schedules;
            this.updateActiveLiveEvents();
            this.ready = true;
        }
    }

    useTimezone: string = 'local';

    async switchTimezone(tz) {
        this.useTimezone = tz;
        this.liveSchedules = null;
        await this.fetchSchedule();
    }

    get generalTimeZoneString() {
        if (this.timezoneString === 'PST' || this.timezoneString === 'PDT') {
            return 'PT';
        }
        if (this.timezoneString === 'EST' || this.timezoneString === 'EDT') {
            return 'ET';
        }
        return null;
    }

    activeLiveEvents: ApiLiveSchedule[];
    adhocStreams: ApiLiveStream[];

    updateActiveLiveEvents() {
        let events = {};

        if (!this.allStreams) {
            this.activeLiveEvents = [];
            return;
        }

        for (let stream of this.allStreams) {
            if (stream.active_live_event) {
                if (!events[stream.active_live_event.id])
                    events[stream.active_live_event.id] = stream.active_live_event;
            }
        }

        this.activeLiveEvents = Object.keys(events).map(x => events[x]);
    }

    get hasLiveItems() {
        return (this.adhocStreams && this.adhocStreams.length > 0)
            || (this.activeLiveEvents && this.activeLiveEvents.length > 0);
    }

    getDayDisplay(day: string) {
        const days = {
            sunday: 'Sunday',
            monday: 'Monday',
            tuesday: 'Tuesday',
            wednesday: 'Wednesday',
            thursday: 'Thursday',
            friday: 'Friday'
        };

        return days[day] ?? day;
    }

    async fetchData() {
        if (this.hasServerState)
            return;

        this.allStreams = await this.loadData(() => this.liveStreamsService.active());
        this.adhocStreams = this.allStreams.filter(x => !x.live_events || (x.live_events as any).length == 0);

        // this.adhocStreams = this.allStreams; // DEBUG

        this.featuredStream = this.allStreams[0];
        this.otherStreams = this.allStreams.slice(1);
        this.ready = true;
        this.updateActiveLiveEvents();

        if (isClientSide()) {
            setTimeout(() => {
                let days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
                let today = days[new Date().getDay()];
                let feedTag = document.querySelector(`[data-day-anchor="${today}"]`);

                if (!feedTag)
                    return;

                if (document.body.scrollTop < 5) {

                    if (feedTag.nextElementSibling) {
                        feedTag.nextElementSibling.scrollIntoView({
                            block: "center"
                        });
                    } else {
                        feedTag.scrollIntoView({
                            block: "start"
                        });
                    }
                }
            }, 50);
       }
    }
}
